import { useState, useCallback, useRef, useEffect } from "react"

/**
 * Enterprise v2 checkbox key - must be created as a checkbox-type key in Google Cloud Console
 *
 * Note: Google reCAPTCHA Enterprise requires separate keys for different challenge types:
 * - Score-based keys (v3): Can only be used with grecaptcha.enterprise.execute()
 * - Checkbox keys (v2): Can only be used with grecaptcha.enterprise.render()
 *
 * Attempting to use a score-based key with render() will result in "Invalid key type" error.
 * Both keys use the same Enterprise API namespace, preserving grecaptcha.enterprise.execute()
 * functionality when the v2 script loads.
 */
const RECAPTCHA_V2_SITE_KEY = process.env.GATSBY_RECAPTCHA_V2_SITE_KEY || ""

/**
 * Hook for managing reCAPTCHA v2 fallback functionality
 * Handles v2 visibility state, script loading coordination, token lifecycle, and re-submission flow
 */
export const useRecaptchaFallback = () => {
  const [isV2Visible, setIsV2Visible] = useState(false)
  const [v2Token, setV2Token] = useState(null)
  const [isV2Loading, setIsV2Loading] = useState(false)
  const [v2Error, setV2Error] = useState(null)
  const [isV2Ready, setIsV2Ready] = useState(false)
  const v2WidgetId = useRef(null)
  const v2ContainerRef = useRef(null)

  /**
   * Check if reCAPTCHA v2 Enterprise is available
   */
  const checkV2Ready = useCallback(() => {
    return !!(
      window.grecaptcha &&
      window.grecaptcha.enterprise &&
      typeof window.grecaptcha.enterprise.render === "function" &&
      typeof window.grecaptcha.enterprise.reset === "function"
    )
  }, [])

  /**
   * Initialize reCAPTCHA v2 widget
   */
  const initializeV2Widget = useCallback(() => {
    if (!RECAPTCHA_V2_SITE_KEY) {
      console.error("Missing GATSBY_RECAPTCHA_V2_SITE_KEY")
      setV2Error("reCAPTCHA v2 configuration error.")
      return
    }

    if (!v2ContainerRef.current) {
      console.error("v2 container ref not available")
      return
    }

    if (v2WidgetId.current !== null) {
      // Widget already initialized
      return
    }

    try {
      // Render v2 widget using Enterprise API with checkbox-type key
      v2WidgetId.current = window.grecaptcha.enterprise.render(v2ContainerRef.current, {
        sitekey: RECAPTCHA_V2_SITE_KEY,
        callback: token => {
          setV2Token(token)
          setV2Error(null)
        },
        "error-callback": () => {
          console.error("reCAPTCHA v2 error")
          setV2Error("reCAPTCHA verification error. Please try again.")
        },
        "expired-callback": () => {
          setV2Token(null)
          setV2Error("reCAPTCHA expired. Please verify again.")
        },
      })

      setIsV2Ready(true)
    } catch (error) {
      console.error("Failed to initialize reCAPTCHA v2 widget:", error)
      setV2Error(
        "Failed to initialize reCAPTCHA. Please refresh and try again."
      )
    }
  }, [])

  /**
   * Load reCAPTCHA v2 script if not already loaded
   */
  const loadV2Script = useCallback(async () => {
    // Check if v2 API is already available
    if (checkV2Ready()) {
      setIsV2Ready(true)
      return
    }

    setIsV2Loading(true)
    setV2Error(null)

    // Check if script already exists (from v3 Enterprise)
    const existingScript = document.querySelector("#recaptcha-script")
    if (existingScript) {
      // Enterprise script loaded, check if Enterprise v2 API is available
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        // Check if v2 Enterprise render/reset methods are available
        if (!checkV2Ready()) {
          // Need to load Enterprise v2 script with explicit render mode
          const v2Script = document.createElement("script")
          v2Script.src = `https://www.google.com/recaptcha/enterprise.js?render=explicit`
          v2Script.async = true
          v2Script.id = "recaptcha-v2-enterprise-script"

          v2Script.onload = () => {
            setIsV2Loading(false)
            setIsV2Ready(true)
          }

          v2Script.onerror = () => {
            setIsV2Loading(false)
            setV2Error(
              "Failed to load reCAPTCHA v2. Please check your connection."
            )
          }

          document.body.appendChild(v2Script)
          return
        }
      }
    }

    // No existing script, load Enterprise v2 from scratch
    const v2Script = document.createElement("script")
    v2Script.src = `https://www.google.com/recaptcha/enterprise.js?render=explicit`
    v2Script.async = true
    v2Script.id = "recaptcha-v2-enterprise-script"

    v2Script.onload = () => {
      setIsV2Loading(false)
      setIsV2Ready(true)
    }

    v2Script.onerror = () => {
      setIsV2Loading(false)
      setV2Error("Failed to load reCAPTCHA v2. Please check your connection.")
    }

    document.body.appendChild(v2Script)
  }, [checkV2Ready])

  /**
   * Show v2 challenge
   */
  const showV2Challenge = useCallback(async () => {
    setIsV2Visible(true)

    // Load script if needed
    if (!isV2Ready) {
      await loadV2Script()
    }
  }, [isV2Ready, loadV2Script])

  /**
   * Reset v2 widget
   */
  const resetV2 = useCallback(() => {
    if (v2WidgetId.current !== null && window.grecaptcha && window.grecaptcha.enterprise) {
      try {
        window.grecaptcha.enterprise.reset(v2WidgetId.current)
      } catch (error) {
        console.error("Failed to reset reCAPTCHA v2:", error)
      }
    }
    setV2Token(null)
    setV2Error(null)
  }, [])

  /**
   * Hide v2 challenge
   */
  const hideV2Challenge = useCallback(() => {
    setIsV2Visible(false)
    resetV2()
  }, [resetV2])

  /**
   * Initialize widget when ready and visible
   */
  useEffect(() => {
    if (
      isV2Visible &&
      isV2Ready &&
      v2ContainerRef.current &&
      v2WidgetId.current === null
    ) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        initializeV2Widget()
      }, 100)
    }
  }, [isV2Visible, isV2Ready, initializeV2Widget])

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (v2WidgetId.current !== null) {
        resetV2()
      }
    }
  }, [resetV2])

  return {
    // State
    isV2Visible,
    v2Token,
    isV2Loading,
    v2Error,
    isV2Ready,

    // Actions
    showV2Challenge,
    hideV2Challenge,
    resetV2,

    // Refs
    v2ContainerRef,

    // Utils
    setV2Token,
    setV2Error,
  }
}
